<script setup>
import { useI18n } from 'vue-i18n'
import TrophyCard from '@/components/TrophyCard.vue'

/**
 * Trophy cabinet: the awards to show as cards, and nothing else.
 *
 * Like EventList it only renders the list it is given (loading and error states stay with the
 * caller), so the same component shows one player's cabinet and, with `showPlayer`, the awards of
 * everybody on a general hall of fame.
 */
defineProps({
  /** Awards in the shape built by App\Libraries\EventAwardLib. */
  awards: { type: Array, default: () => [] },
  /** Name the winner of each award: needed by the general hall of fame, not by a player page. */
  showPlayer: { type: Boolean, default: false },
})

const { t } = useI18n()
</script>

<template>
  <v-card elevation="3" rounded="lg" class="overflow-hidden">
    <div class="d-flex align-center ga-3 pa-4">
      <v-avatar color="primary" variant="tonal" size="48">
        <v-icon size="28">mdi-trophy-variant</v-icon>
      </v-avatar>
      <div class="flex-grow-1">
        <h2 class="text-h6 font-weight-bold">{{ $t('awards.title') }}</h2>
        <!-- <p class="text-caption text-medium-emphasis mb-0">{{ $t('awards.subtitle') }}</p> -->
      </div>
      <v-chip v-if="awards.length" variant="tonal" label>{{ awards.length }}</v-chip>
    </div>

    <v-divider />

    <div v-if="!awards.length" class="pa-6 text-center text-medium-emphasis">
      <v-icon size="36" class="d-block mx-auto mb-2">mdi-trophy-outline</v-icon>
      {{ $t('awards.empty') }}
    </div>

    <div v-else class="cabinet">
      <TrophyCard
        v-for="award in awards"
        :key="award.id"
        :award="award"
        :show-player="showPlayer"
      />
    </div>
  </v-card>
</template>

<style scoped>
/* The wall: as many trophies on a row as the width allows, each one the same compact size. */
.cabinet {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
}
</style>
